// Shared per-style ACE row + header for the CPSC broker handoff (CHA-233). Client (ESM) mirror of the
// server module functions/cpscBrokerCsv.js — the row/header shape lives in ONE logical place so the daily-
// broker-report cron's CSV and the UI's "Export for ACE" CSV can never drift (§8.2). A CRA bundle and a
// Cloud Functions CommonJS bundle can't share one file (same twin-file pattern as csvSafe.js), so this
// file MUST be kept byte-identical to the server copy for ACE_HEADERS + aceCellsForStyle.
const s = (v) => String(v == null ? '' : v).trim();
const fmtDate = (d) => (s(d) ? s(d).slice(0, 10) : '—');

// CHA-228 column set — one row per (commercial invoice × style).
// CHA-243 — Product ID + Certifier ID added adjacent to CPSC CoC Version so the ACE filing identifiers stay
// contiguous. CHA-242 — dropped the vestigial, always-blank 'CPSC Certificate ID' col: the ACE cert reference
// is the 3-part composite {Product ID, CPSC CoC Version (Version ID), Certifier ID}, with no separate cert id.
// Cluster: Report Number · CPSC CoC Version · Product ID · Certifier ID · Lab CPSC ID.
// CHA-246 — the two flammability-EXEMPTION disclaim columns appended at the end (Exemption CFR + Exemption
// Disclaim). Populated FROM the exemption record's engine basis (see exemptionCfr/exemptionDisclaim below);
// blank for any non-exempt style. Appended (never inserted) so the existing 26-column positions are unchanged.
export const ACE_HEADERS = ['Commercial Invoice', 'Division', 'Customer', 'POs', 'Ship Date', 'HTS', 'Style', 'Product Name', 'PO', 'Terms', 'Warehouse', 'Shipment No', 'Container', 'Units', 'CPSC Required', 'Cert Status', 'Cert Status Note', 'Report Number', 'Version ID (ACE)', 'Product ID (ACE)', 'Certifier ID (ACE)', 'Lab CPSC ID', 'Citations (CFR)', 'Manufacture Date', 'Last Test Date', 'Manufacturer', 'Exemption CFR', 'Exemption Disclaim'];

// CHA-246 — the broker flammability-exemption disclaim tokens, derived STRICTLY from the exemption record's
// engine basis (`st.exemptionPath`, set by rollupInvoices from the ACTIVE exemption record) — NEVER guessed,
// NEVER hardcoded per style. Sub-prong A/B name the exact 16 CFR 1610.1(d) clause; a MANUAL (operator-declared
// GCC) states the SECTION only (a declared GCC asserts 1610.1(d), not which sub-prong). A style with no ACTIVE
// exemption (`st.exempt !== true`) or an unknown/NONE path → BOTH cells blank (fail-closed). Two columns (CFR +
// Disclaim) so the broker drops each token straight into its matching ACE field without splitting a combined
// string. Byte-identical with functions/cpscBrokerCsv.js (keep in sync).
export const EXEMPTION_CFR = { A: '16 CFR 1610.1(d)(1)', B: '16 CFR 1610.1(d)(2)', MANUAL: '16 CFR 1610.1(d)' };
const EXEMPTION_DISCLAIM = 'Disclaim B / 130.006';
const exemptionCfr = (st) => (st && st.exempt === true ? (EXEMPTION_CFR[s(st.exemptionPath)] || '') : '');
const exemptionDisclaim = (st) => (exemptionCfr(st) ? EXEMPTION_DISCLAIM : '');

// CHA-222 PR3 — a real cert wins; else a flammability exemption reads EXEMPT (satisfied) even when a stale
// non-certified draft exists; else the draft status; else MISSING / n-a.
export const aceCertStatus = (st) => (
  st.certified ? (s(st.certStatus) || 'certified')
    : st.exempt ? 'EXEMPT'
      : st.notSubject ? 'NOT SUBJECT'
      /* CHA-281 — an unruled review-level style is PENDING the adult/children's ruling: never exported as a
         positive not-required assertion (fail-safe: 'n/a' would be indistinguishable from an adult ruling). */
      : st.reviewPending ? 'NEEDS RULING'
        : (s(st.certStatus) || (st.requiresCpsc ? 'MISSING' : 'n/a'))
);

// PURE — the 28-cell ACE row for one style on one invoice. MUST stay byte-identical with the server copy.
export function aceCellsForStyle(inv, st) {
  const pos = (inv.pos || []).join(' ');
  // Defense in depth: explicit no-certificate determinations must never export identifiers from a stale,
  // non-certified draft even if a caller supplies them. A certified row cannot also be exempt/not-subject.
  const certRefAllowed = !st.exempt && !st.notSubject;
  return [
    inv.commInv, inv.division || '', inv.customer || '', pos, fmtDate(inv.shipDate),
    st.hts || '', st.sty, certRefAllowed ? (st.certProductName || '') : '',
    st.po || '', st.terms || '', st.warehouse || '', st.shipmentNum || '', st.container || '', // CHA-228
    st.units || 0,
    st.notSubject ? 'no' : st.requiresCpsc ? 'YES' : st.reviewPending ? 'REVIEW PENDING' : 'no', // CHA-540 — explicit TTS determination means no CPSC rule applies
    aceCertStatus(st),
    st.certStatusNote || '',
    certRefAllowed ? (st.certReportNo || '') : '', certRefAllowed ? (st.certVersionId || '') : '',
    certRefAllowed ? (st.resolvedProductId || '') : '', certRefAllowed ? (st.certifierId || '') : '', // CHA-243 Product ID + Certifier ID
    certRefAllowed ? (st.certLabCpscId || '') : '', certRefAllowed ? (st.certCitations || []).join(' ') : '',
    certRefAllowed ? (st.certManufactureDate || '') : '', certRefAllowed ? (st.certLastTestDate || '') : '',
    certRefAllowed ? (st.certManufacturer || '') : '',
    exemptionCfr(st), exemptionDisclaim(st), // CHA-246 — exemption disclaim basis (blank unless ACTIVE-exempt)
  ];
}
